import { action } from "typesafe-actions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import { IServiceCategory, IServiceItem } from "./service.types";

// ==================== SERVICE CATEGORY ACTIONS ====================

export const FETCH_SERVICE_CATEGORY_LIST_PROGRESS =
  "FETCH_SERVICE_CATEGORY_LIST_PROGRESS";
export const FETCH_SERVICE_CATEGORY_LIST_SUCCESS =
  "FETCH_SERVICE_CATEGORY_LIST_SUCCESS";
export const FETCH_SERVICE_CATEGORY_LIST_FAILED =
  "FETCH_SERVICE_CATEGORY_LIST_FAILED";

export const fetchServiceCategoryListProgress = () =>
  action(FETCH_SERVICE_CATEGORY_LIST_PROGRESS);
export const fetchServiceCategoryListSuccess = (
  data: IServiceCategory[],
  totalRecords: number
) => action(FETCH_SERVICE_CATEGORY_LIST_SUCCESS, { data, totalRecords });
export const fetchServiceCategoryListFailed = () =>
  action(FETCH_SERVICE_CATEGORY_LIST_FAILED);

export const fetchServiceCategoryListAsync =
  (
    pageNumber: number,
    itemPerPage: number,
    pageLimit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    companyOutletUuid?: string | null,
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchServiceCategoryListProgress());
    try {
      dispatch(saveLoaderProgress());
      let searchQuery = "";

      if (status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      if (companyOutletUuid) {
        searchQuery += `&company_outlet_uuid=${companyOutletUuid}`;
      }

      if ((searchValue?.length ?? 0) > 0 || searchByName) {
        const advanceFilter = [...(searchValue || [])];

        if (searchByName) {
          advanceFilter.push({
            column: ["service_category_name"],
            value: searchByName,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      const response = await api.get(
        `/service/get-service-category?pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${itemPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}` +
          searchQuery
      );

      dispatch(
        fetchServiceCategoryListSuccess(
          response.data.data,
          response.data.totalRecords
        )
      );
    } catch (error: any) {
      dispatch(fetchServiceCategoryListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            error?.response?.data?.message ||
            "Failed to fetch service categories",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertServiceCategoryAsync =
  (
    data: Partial<IServiceCategory>,
    onCallback?: (isSuccess: boolean, data?: IServiceCategory) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      // Ensure company_outlet_uuid is present
      if (!data.company_outlet_uuid) {
        throw new Error("company_outlet_uuid is required");
      }

      const response = await api.post("/service/upsert-service-category", data);
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message:
            response.data.message || "Service category saved successfully",
          displayAs: "snackbar",
        })
      );
      onCallback?.(true, response.data.data);
      return response.data;
    } catch (error: any) {
      dispatch(saveLoaderCompleted());
      onCallback?.(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            error?.response?.data?.message || "Failed to save service category",
          displayAs: "snackbar",
        })
      );
      throw error;
    }
  };

// ==================== SERVICE ITEM ACTIONS ====================

export const FETCH_SERVICE_ITEM_LIST_PROGRESS =
  "FETCH_SERVICE_ITEM_LIST_PROGRESS";
export const FETCH_SERVICE_ITEM_LIST_SUCCESS =
  "FETCH_SERVICE_ITEM_LIST_SUCCESS";
export const FETCH_SERVICE_ITEM_LIST_FAILED = "FETCH_SERVICE_ITEM_LIST_FAILED";

export const fetchServiceItemListProgress = () =>
  action(FETCH_SERVICE_ITEM_LIST_PROGRESS);
export const fetchServiceItemListSuccess = (
  data: IServiceItem[],
  totalRecords: number
) => action(FETCH_SERVICE_ITEM_LIST_SUCCESS, { data, totalRecords });
export const fetchServiceItemListFailed = () =>
  action(FETCH_SERVICE_ITEM_LIST_FAILED);

export const fetchServiceItemListAsync =
  (
    pageNumber: number,
    itemPerPage: number,
    pageLimit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    companyOutletUuid?: string | null,
    serviceCategoryUuid?: string | null,
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string,
    service_item_uuid?: string,
    onCallBack?: (isSuccess: boolean, data: IServiceItem[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchServiceItemListProgress());
    // Store callback data to call AFTER finally block completes
    let callbackData: { isSuccess: boolean; data: IServiceItem[] } = {
      isSuccess: false,
      data: [],
    };

    try {
      dispatch(saveLoaderProgress());
      let searchQuery = "";

      if (service_item_uuid) {
        searchQuery += `&service_item_uuid=${service_item_uuid}`;
      }

      if (status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      if (companyOutletUuid) {
        searchQuery += `&company_outlet_uuid=${companyOutletUuid}`;
      }

      if (serviceCategoryUuid) {
        searchQuery += `&service_category_uuid=${serviceCategoryUuid}`;
      }

      if ((searchValue?.length ?? 0) > 0 || searchByName) {
        const advanceFilter = [...(searchValue || [])];

        if (searchByName) {
          advanceFilter.push({
            column: ["service_category_name", "description"],
            value: searchByName,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      const response = await api.get(
        `/service/get-service-item?pageNo=${pageNumber}&pageLimit=${pageLimit}&itemPerPage=${itemPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}` +
          searchQuery
      );

      callbackData = { isSuccess: true, data: response.data.data };
      dispatch(
        fetchServiceItemListSuccess(
          response.data.data,
          response.data.totalRecords
        )
      );
    } catch (error: any) {
      callbackData = { isSuccess: false, data: [] };
      dispatch(fetchServiceItemListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            error?.response?.data?.message || "Failed to fetch service items",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }

    // Call callback AFTER finally block - this ensures nested actions
    // can properly manage their own loaders without being interrupted
    onCallBack?.(callbackData.isSuccess, callbackData.data);
  };

export const upsertServiceItemAsync =
  (
    data: Partial<IServiceItem>,
    onCallback?: (isSuccess: boolean, data?: IServiceItem) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      // Ensure company_outlet_uuid is present
      if (!data.company_outlet_uuid) {
        throw new Error("company_outlet_uuid is required");
      }

      const response = await api.post("/service/upsert-service-item", data);
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: response.data.message || "Service item saved successfully",
          displayAs: "snackbar",
        })
      );
      onCallback?.(true, response.data.data);
      return response.data;
    } catch (error: any) {
      dispatch(saveLoaderCompleted());
      onCallback?.(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            error?.response?.data?.message || "Failed to save service item",
          displayAs: "snackbar",
        })
      );
      throw error;
    }
  };

// ==================== CLEAR STATE ACTION ====================

export const CLEAR_SERVICE_STATE = "CLEAR_SERVICE_STATE";
export const clearServiceState = () => action(CLEAR_SERVICE_STATE);
